import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { aftercareEntries } from "./aftercareData";
import useDocumentMeta from "../../utils/useDocumentMeta";

const isSectionHeading = (text) => {
  if (typeof text !== "string") return false;
  return /^[A-Z0-9+\s]+$/.test(text.trim());
};

const Aftercare = () => {
  useDocumentMeta(
    "Aftercare Guidance | Radiance Aesthetics | Hindley | Wigan",
    "Post-treatment aftercare advice for microneedling, chemical peels and dermaplaning at Radiance Aesthetics, Hindley, Wigan.",
  );

  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: "var(--color-bg-warm)",
        padding: { xs: "32px 16px", md: "60px 24px" },
        minHeight: "60vh",
      }}
    >
      <Stack
        sx={{
          maxWidth: "720px",
          margin: "0 auto",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "var(--color-accent)",
            textAlign: "center",
            mb: 1.5,
          }}
        >
          Post-Treatment Care
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-primary)",
            fontWeight: 400,
            textAlign: "center",
            fontSize: { xs: "28px", md: "40px" },
            mb: 1.5,
          }}
          variant="h1"
        >
          Aftercare Guidance
        </Typography>

        <Typography
          sx={{
            color: "var(--color-text-light)",
            textAlign: "center",
            fontSize: "16px",
            mb: 5,
            maxWidth: "600px",
            lineHeight: 1.7,
            fontFamily: "var(--font-body)",
          }}
        >
          Please follow the guidance below to support healing and achieve the
          best results from your treatment.
        </Typography>

        <Stack gap={2} sx={{ width: "100%" }}>
          {aftercareEntries.map((entry) => (
            <Accordion
              key={entry.id}
              expanded={expanded === entry.id}
              onChange={handleChange(entry.id)}
              disableGutters
              sx={{
                borderRadius: "8px !important",
                border: "1px solid var(--color-border)",
                boxShadow: "none",
                overflow: "hidden",
                "&:before": { display: "none" },
                "&.Mui-expanded": {
                  boxShadow: "0 2px 12px rgba(90, 77, 71, 0.08)",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: "var(--color-primary)" }} />
                }
                sx={{ px: 2.5, py: 0.5 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "var(--color-primary)",
                    fontFamily: "var(--font-body)",
                    lineHeight: 1.3,
                  }}
                >
                  {entry.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, py: 2 }}>
                <Stack gap={1.25}>
                  {entry.items.map((text, idx) => {
                    if (isSectionHeading(text)) {
                      return (
                        <Typography
                          key={idx}
                          sx={{
                            fontFamily: "var(--font-body)",
                            fontSize: "12px",
                            fontWeight: 700,
                            letterSpacing: "1.5px",
                            textTransform: "uppercase",
                            color: "var(--color-accent)",
                            mt: idx === 0 ? 0 : 2,
                            mb: 0.5,
                          }}
                        >
                          {text}
                        </Typography>
                      );
                    }

                    return (
                      <Typography
                        key={idx}
                        sx={{
                          fontSize: "14px",
                          lineHeight: 1.7,
                          color: "var(--color-text-light)",
                          fontFamily: "var(--font-body)",
                          pl: 1.5,
                          borderLeft: "2px solid var(--color-border)",
                        }}
                      >
                        {text}
                      </Typography>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 4,
            p: 2,
            backgroundColor: "var(--color-white)",
            borderRadius: "8px",
            width: "100%",
            border: "1px solid var(--color-border)",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "var(--color-text-muted)",
              fontStyle: "italic",
              textAlign: "center",
              fontFamily: "var(--font-body)",
            }}
          >
            If you have any concerns following your treatment, please don't
            hesitate to contact us.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default Aftercare;
